import { useState } from 'react';
import { UserCircle, Check, X, SpinnerGap } from '@phosphor-icons/react';
import type { ProfileUpdateEvent, ProfileUpdateProposal } from '@researcher/shared';
import {
  getHealthProfile,
  updateHealthProfile,
  createTreatment,
  updateHealthGoals,
} from '../../utils/healthProfile';
import { createPeriodEntries } from '../../utils/healthLog';
import { fireNotificationValueTrigger } from '../NotificationOptIn';

interface ProfileUpdateCardProps {
  event: ProfileUpdateEvent;
  savedStatus?: 'applied' | 'dismissed';
  onStatusChange?: (status: 'applied' | 'dismissed') => void;
}

const ACTION_LABELS: Record<string, string> = {
  add_condition: 'Add condition',
  add_suspected_condition: 'Add suspected condition',
  add_allergy: 'Add allergy',
  update_reproductive_status: 'Update reproductive status',
  add_treatment: 'Add treatment',
  add_health_goal: 'Add health goal',
  add_family_history: 'Add family history',
  setup_cycle: 'Set up cycle tracking',
  update_height: 'Update height',
  record_pregnancy_history: 'Record pregnancy',
};

const OUTCOME_LABELS: Record<string, string> = {
  live_birth: 'live birth',
  miscarriage: 'miscarriage',
  stillbirth: 'stillbirth',
  ectopic: 'ectopic pregnancy',
  termination: 'termination',
};

function proposalLabel(p: ProfileUpdateProposal): string {
  const action = ACTION_LABELS[p.action] || p.action;
  const parts = [action];
  if (p.name) parts.push(`"${p.name}"`);
  if (p.relation) parts.push(`(${p.relation})`);
  if (p.status) parts.push(`(${p.status})`);
  if (p.diagnosedYear) parts.push(`diagnosed ${p.diagnosedYear}`);
  if (p.dosage) parts.push(`— ${p.dosage}`);
  if (p.description) parts.push(`— ${p.description}`);
  if (p.targetDate) parts.push(`by ${p.targetDate}`);
  if (p.milestones?.length) parts.push(`(${p.milestones.length} milestone${p.milestones.length > 1 ? 's' : ''})`);
  if (p.priority) parts.push(`[${p.priority} priority]`);
  if (p.pregnancyDueDate) parts.push(`due ${new Date(p.pregnancyDueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
  if (p.pregnancyOutcome) parts.push(`(${OUTCOME_LABELS[p.pregnancyOutcome] || p.pregnancyOutcome})`);
  if (p.pregnancyDate) parts.push(new Date(p.pregnancyDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
  if (p.lastPeriodDate) parts.push(`last period ${new Date(p.lastPeriodDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
  if (p.typicalCycleLength) parts.push(`${p.typicalCycleLength}-day cycle`);
  if (p.typicalPeriodLength) parts.push(`${p.typicalPeriodLength}-day period`);
  if (p.height != null) {
    const unit = p.heightUnit || 'in';
    if (unit === 'in') {
      const ft = Math.floor(p.height / 12);
      const inches = p.height % 12;
      parts.push(`${ft}'${inches}"`);
    } else {
      parts.push(`${p.height} cm`);
    }
  }
  return parts.join(' ');
}

export default function ProfileUpdateCard({ event, savedStatus, onStatusChange }: ProfileUpdateCardProps) {
  const [status, setStatus] = useState<'pending' | 'applying' | 'applied' | 'dismissed'>(
    savedStatus || 'pending'
  );
  const [error, setError] = useState<string | null>(null);

  if (status === 'dismissed') return null;

  const handleAccept = async () => {
    setStatus('applying');
    setError(null);

    try {
      const { healthProfile, healthGoals } = await getHealthProfile();

      const currentConditions = healthProfile?.conditions ?? [];
      const currentSuspected = healthProfile?.suspectedConditions ?? [];
      const currentAllergies = healthProfile?.allergies ?? [];
      const currentFamilyHistory = healthProfile?.familyHistory ?? [];
      const currentPregnancyHistory = healthProfile?.pregnancyHistory ?? [];

      const profileUpdates: Record<string, any> = {};
      const treatmentsToCreate: ProfileUpdateProposal[] = [];
      const goalsToCreate: ProfileUpdateProposal[] = [];

      for (const p of event.proposals) {
        switch (p.action) {
          case 'add_condition': {
            if (p.name && !currentConditions.some(c => c.name.toLowerCase() === p.name!.toLowerCase())) {
              const conditions = [
                ...currentConditions,
                {
                  name: p.name,
                  status: (p.status as 'active' | 'managed' | 'resolved' | 'in_diagnosis') || 'active',
                  ...(p.diagnosedYear != null && { diagnosedYear: p.diagnosedYear }),
                },
              ];
              profileUpdates.conditions = conditions;
            }
            break;
          }
          case 'add_suspected_condition': {
            if (p.name && !currentSuspected.some(s => s.toLowerCase() === p.name!.toLowerCase())) {
              profileUpdates.suspectedConditions = [...currentSuspected, p.name];
            }
            break;
          }
          case 'add_allergy': {
            if (p.name && !currentAllergies.some(a => a.toLowerCase() === p.name!.toLowerCase())) {
              profileUpdates.allergies = [...currentAllergies, p.name];
            }
            break;
          }
          case 'update_reproductive_status': {
            if (p.status) {
              profileUpdates.reproductiveStatus = p.status;
            }
            if (p.pregnancyDueDate) {
              profileUpdates.pregnancyDueDate = p.pregnancyDueDate;
            }
            if (p.pregnancyConceptionDate) {
              profileUpdates.pregnancyConceptionDate = p.pregnancyConceptionDate;
            }
            break;
          }
          case 'add_treatment': {
            treatmentsToCreate.push(p);
            break;
          }
          case 'add_health_goal': {
            goalsToCreate.push(p);
            break;
          }
          case 'add_family_history': {
            if (p.name && p.relation) {
              const isDuplicate = currentFamilyHistory.some(
                fh => fh.condition.toLowerCase() === p.name!.toLowerCase() && fh.relation.toLowerCase() === p.relation!.toLowerCase()
              );
              if (!isDuplicate) {
                profileUpdates.familyHistory = [
                  ...currentFamilyHistory,
                  { condition: p.name, relation: p.relation, notes: p.description || null },
                ];
              }
            }
            break;
          }
          case 'update_height': {
            if (p.height != null) {
              profileUpdates.height = p.height;
              profileUpdates.heightUnit = p.heightUnit || 'in';
            }
            break;
          }
          case 'record_pregnancy_history': {
            if (p.pregnancyOutcome && p.pregnancyDate) {
              // Accumulate across proposals in this event (e.g. two births at once),
              // de-duping against existing entries by outcome + date.
              const base = profileUpdates.pregnancyHistory ?? currentPregnancyHistory;
              const exists = base.some((e: any) => e.outcome === p.pregnancyOutcome && e.date === p.pregnancyDate);
              if (!exists) {
                profileUpdates.pregnancyHistory = [
                  ...base,
                  {
                    id: `pg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                    outcome: p.pregnancyOutcome,
                    date: p.pregnancyDate,
                    notes: null,
                  },
                ];
              }
            }
            break;
          }
          case 'setup_cycle': {
            if (p.lastPeriodDate) {
              const cycleLength = p.typicalCycleLength || 28;
              const periodLength = p.typicalPeriodLength || 5;
              profileUpdates.lastPeriodDate = p.lastPeriodDate;
              profileUpdates.typicalCycleLength = cycleLength;
              profileUpdates.typicalPeriodLength = periodLength;
              profileUpdates.cycleSetupCompleted = true;

              // Create bleeding log entries for the last period
              const today = new Date().toISOString().split('T')[0];
              const endD = new Date(p.lastPeriodDate + 'T00:00:00');
              endD.setDate(endD.getDate() + periodLength - 1);
              const y = endD.getFullYear();
              const m = String(endD.getMonth() + 1).padStart(2, '0');
              const day = String(endD.getDate()).padStart(2, '0');
              let endDateStr = `${y}-${m}-${day}`;
              if (endDateStr > today) endDateStr = today;
              await createPeriodEntries(p.lastPeriodDate, endDateStr, 'medium');
            }
            break;
          }
        }
      }

      // Apply profile updates
      if (Object.keys(profileUpdates).length > 0) {
        await updateHealthProfile(profileUpdates);
      }

      // Create treatments
      for (const t of treatmentsToCreate) {
        await createTreatment({
          name: t.name || 'Unknown treatment',
          type: (t.type as any) || 'medication',
          status: 'active',
          dosage: t.dosage || null,
          startDate: t.startDate || new Date().toISOString().split('T')[0],
          sideEffects: [],
          source: 'manual',
        });
      }

      // Create goals
      if (goalsToCreate.length > 0) {
        const existingGoals = healthGoals ?? [];
        const newGoals = goalsToCreate.map(g => ({
          id: `goal_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          type: (g.type as any) || 'other',
          description: g.description || g.name || 'Health goal',
          status: 'active' as const,
          ...(g.targetDate && { targetDate: g.targetDate }),
          ...(g.milestones && {
            milestones: g.milestones.map(m => ({
              id: `ms_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
              description: m.description,
              completed: false,
            })),
          }),
          ...(g.priority && { priority: g.priority }),
          createdAt: new Date(),
          updatedAt: new Date(),
        }));
        await updateHealthGoals([...existingGoals, ...newGoals]);
        fireNotificationValueTrigger('first_goal');
      }

      setStatus('applied');
      onStatusChange?.('applied');
    } catch (err: any) {
      console.error('[ProfileUpdateCard] Failed to apply:', err);
      setError(err.message || 'Failed to update profile');
      setStatus('pending');
    }
  };

  const handleDismiss = () => {
    setStatus('dismissed');
    onStatusChange?.('dismissed');
  };

  return (
    <div className="mt-3 rounded-xl border border-pink-200 dark:border-pink-800 bg-orange-50 dark:bg-gray-800 overflow-hidden not-prose">
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <UserCircle size={18} weight="duotone" className="text-pink-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Profile Update Suggestion
          </span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
          {event.summary}
        </p>
        <ul className="space-y-1 mb-3">
          {event.proposals.map((p, i) => (
            <li key={i} className="text-xs text-gray-700 dark:text-gray-200 flex items-start gap-1.5">
              <span className="text-pink-500 mt-0.5 flex-shrink-0">+</span>
              {proposalLabel(p)}
            </li>
          ))}
        </ul>

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>
        )}

        {status === 'applied' ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
            <Check size={14} weight="bold" />
            Profile updated
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleAccept}
              disabled={status === 'applying'}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-60"
            >
              {status === 'applying' ? (
                <SpinnerGap size={14} className="animate-spin" />
              ) : (
                <Check size={14} weight="bold" />
              )}
              {status === 'applying' ? 'Updating...' : 'Accept'}
            </button>
            <button
              onClick={handleDismiss}
              disabled={status === 'applying'}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
            >
              <X size={14} />
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
